import React from 'react';
import { useForm } from './form-provider';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios'

// Add these utility functions at the top of your file
const encodeParams = (params) => {
  const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value && value !== '') {
      if (key === 'n') {
        // Sanitize name
        acc[key] = String(value).trim().substring(0, 50) || 'Guest';
      } else {
        acc[key] = value;
      }
    }
    return acc;
  }, {});

  return btoa(JSON.stringify(cleanParams))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

export function StepThree() {
  const { formData, updateFormData, setStep, setPagination } = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const [activeFields, setActiveFields] = React.useState(
    Object.entries(formData.testScores || {})
      .filter(([key]) => !key.startsWith('ielts_'))
      .length > 0 
      ? Object.keys(formData.testScores)
          .filter(key => !key.startsWith('ielts_'))
          .map(key => ({ 
            id: crypto.randomUUID(), 
            type: key,
            subType: key === 'toefl' ? (formData.testScores[`${key}_type`] || 'ibt') : null
          }))
      : [{ id: crypto.randomUUID(), type: '', subType: null }]
  );

  const [ieltsScores, setIeltsScores] = useState({
    overall: formData.testScores?.ielts_overall || '',
    reading: formData.testScores?.ielts_reading || '',
    writing: formData.testScores?.ielts_writing || '',
    speaking: formData.testScores?.ielts_speaking || '',
    listening: formData.testScores?.ielts_listening || ''
  });


  
  const availableScores = {
    'English Proficiency': [
      { value: 'toefl', label: 'TOEFL' },
      { value: 'pte', label: 'PTE' },
    ],
    'Standardized Tests': [
      { value: 'gmat', label: 'GMAT' },
      { value: 'gre', label: 'GRE' },
      { value: 'sat', label: 'SAT' },
    ],
    'Academic Scores': [
      { value: 'tenthScore', label: '10th Score' },
      { value: 'twelfthScore', label: '12th Score' },
      { value: 'ugCgpa', label: 'UG CGPA' },
    ]
  };
  const updateIeltsScore = (field, value) => {
    const newScores = { ...ieltsScores, [field]: value };
    setIeltsScores(newScores);
    
    // Update form data with individual IELTS scores
    updateFormData({
      testScores: {
        ...formData.testScores,
        [`ielts_${field}`]: value
      }
    });
  };

  const updateTestScore = (id, value, field = 'value') => {
    const fieldData = activeFields.find(f => f.id === id);
    if (!fieldData) return;

    if (field === 'type') {
      const oldType = fieldData.type;
      const newFormData = { ...formData.testScores };
      delete newFormData[oldType];
      if (oldType === 'toefl') {
        delete newFormData['toefl_type'];
      }
      updateFormData({
        testScores: { ...newFormData }
      });
    }

    let newFields;
    if (field === 'type' && value === 'toefl') {
      newFields = activeFields.map(f => 
        f.id === id ? { ...f, [field]: value, subType: 'ibt' } : f
      );
    } else {
      newFields = activeFields.map(f => 
        f.id === id ? { ...f, [field]: value } : f
      );
    }
    setActiveFields(newFields);

    if (field === 'value' && fieldData.type) {
      const newTestScores = { ...formData.testScores };
      newTestScores[fieldData.type] = value;
      if (fieldData.type === 'toefl' && fieldData.subType) {
        newTestScores['toefl_type'] = fieldData.subType;
      }
      updateFormData({ testScores: newTestScores });
    }
  };

  // const getUsedScores = () => activeFields.map(f => f.type).filter(Boolean);
  const addNewField = () => {
    setActiveFields([...activeFields, { id: crypto.randomUUID(), type: '', subType: null }]);
  };

  const removeField = (id) => {
    const fieldToRemove = activeFields.find(f => f.id === id);
    if (fieldToRemove?.type) {
      const newTestScores = { ...formData.testScores };
      delete newTestScores[fieldToRemove.type];
      if (fieldToRemove.type === 'toefl') {
        delete newTestScores['toefl_type'];
      }
      updateFormData({ testScores: newTestScores });
    }
    setActiveFields(activeFields.filter(f => f.id !== id));
  };

  const getUsedScores = () => activeFields.map(f => f.type).filter(Boolean);


  const retryAIRecommendations = async (formData, programs, retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        const aiResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-recommendations`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            formData: formData,
            programs: programs.courses,
          }),
        });

        if (!aiResponse.ok) {
          throw new Error("Failed to fetch AI recommendations");
        }

        return await aiResponse.json();
      } catch (error) {
        console.error(`Attempt ${i + 1} failed:`, error);
        if (i === retries - 1) throw error;
        // Wait for 1 second before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  };

  const handleReset = () => {
    setIsLoading(false);
    updateFormData({
      name: '',
      currentEducation: '',
      educationCountry: '',
      preferredCountry: '',
      studyLevel: '',
      fieldOfStudy: '',
      testScores: {
        
      },
      dreamCareer: '',
      proficientSubjects: ''
    });
    setStep(1);
  };

// A reusable fetch-with-retry helper
const fetchWithRetry = async (url, options, retries = 3, delay = 1000) => {
  let error;
  for (let i = 0; i < retries; i++) {
    try {
      console.log(`Attempt ${i + 1} to fetch ${url}`);
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error in url : ${url}\n status: ${response.status}`);
      }
      return response; // If successful, return the response immediately
    } catch (err) {
      error = err;
      // If not the last attempt, wait and retry
      if (i < retries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }
  // If we exhaust all retries, throw the last caught error
  throw error;
};

// A wrapper to retry your AI recommendations call up to 3 times
const retryAIRecommendationsWithRetry = async (formData, programs, retries = 3, delay = 1000) => {
  let error;
  console.log("Retrying AI recommendations...");
  for (let i = 0; i < retries; i++) {
    try {
      return await retryAIRecommendations(formData, programs);
    } catch (err) {
      error = err;
      if (i < retries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }
  throw error;
};

const handleSubmit = async () => {
  console.log("Submitting form data:", formData);
  setIsLoading(true);

  try {
    // Create complete form data with test scores
    const completeFormData = {
      ...formData,
      preferredCountry: formData.preferredCountry || 'all',
      entryScore: formData.testScores?.ielts_overall || '0',
      universityName: 'all',
      studyLevel: formData.studyLevel || 'all',
      fieldOfStudy: formData.fieldOfStudy || 'all',
      name: formData.name || 'Guest',
      sorts: [],
      searchQuery: ''
    };

    // Save form data
    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/eligibility/form-data`, {
      formData
    });

    // Make the filter-programs fetch call with retry
    const response = await fetchWithRetry(
      `${process.env.REACT_APP_BACKEND_URL}/api/filter-programs`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(completeFormData),
      }
    );

    const programs = await response.json();
    console.log("Received programs:", programs);

    const finalAIResponse = await retryAIRecommendationsWithRetry(
      completeFormData,
      programs
    );

    programs.courses = finalAIResponse;
    setPagination(programs.pagination);

    // Update URL with initial filters
    const params = {
      c: completeFormData.preferredCountry,
      s: completeFormData.entryScore,
      u: completeFormData.universityName,
      l: completeFormData.studyLevel,
      f: completeFormData.fieldOfStudy,
      n: completeFormData.name
    };

    const encoded = encodeParams(params);
    navigate(`/course?q=${encoded}`, {
      state: {
        programs: Array.isArray(programs.courses) ? programs.courses : [],
        searchCriteria: completeFormData,
        name: completeFormData.name,
      },
    });
  } catch (error) {
    console.error("Error:", error);
    setError(true);
    setIsLoading(true);
  }
};


  return (
    <div className="space-y-6">
      {isLoading && (
        <div className='fixed w-screen h-screen bg-white flex flex-col justify-center items-center z-50 top-0 left-0'>
          {!error ? (
            <>
              <img src={"/2.gif"} alt="logo" className='object-contain h-1/2 aspect-auto -mb-10' />
              <h1 className='md:text-xl text-lg animate-pulse'>Finding the best programs for you...</h1>
            </>
          ) : (
            <div className="text-center space-y-4">
              <h1 className="text-xl text-red-600">An error occurred while processing your request</h1>
              <button
                onClick={handleReset}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Go Back
              </button>
            </div>
          )}
        </div>
      )}
      
      <div className="border rounded-lg p-4 space-y-4">
        <h3 className="font-medium text-gray-700">IELTS Scores</h3>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Overall Score</label>
          <input
            type="number"
            step="0.5"
            min="0"
            max="9"
            className="mt-1 w-full rounded-md border-gray-300 border shadow-sm p-1"
            value={ieltsScores.overall}
            onChange={(e) => updateIeltsScore('overall', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Reading</label>
            <input
              type="number"
              step="0.5"
              min="0"
              max="9"
              className="mt-1 w-full rounded-md border-gray-300 border shadow-sm p-1"
              value={ieltsScores.reading}
              onChange={(e) => updateIeltsScore('reading', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Writing</label>
            <input
              type="number"
              step="0.5"
              min="0"
              max="9"
              className="mt-1 w-full rounded-md border-gray-300 border shadow-sm p-1"
              value={ieltsScores.writing}
              onChange={(e) => updateIeltsScore('writing', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Speaking</label>
            <input
              type="number"
              step="0.5"
              min="0"
              max="9"
              className="mt-1 w-full rounded-md border-gray-300 border shadow-sm p-1"
              value={ieltsScores.speaking}
              onChange={(e) => updateIeltsScore('speaking', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Listening</label>
            <input
              type="number"
              step="0.5"
              min="0"
              max="9"
              className="mt-1 w-full rounded-md border-gray-300 border shadow-sm p-1"
              value={ieltsScores.listening}
              onChange={(e) => updateIeltsScore('listening', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Other Test Scores */}
      <div className="space-y-4">
        {activeFields.map((field) => (
          <div key={field.id} className="flex gap-4">
            <select
              className="w-1/2 rounded-md border-gray-300 border shadow-sm p-1"
              value={field.type}
              onChange={(e) => updateTestScore(field.id, e.target.value, 'type')}
            >
              <option value="">Select Test Type</option>
              {Object.entries(availableScores).map(([group, options]) => (
                <optgroup key={group} label={group}>
                  {options
                    .filter(option => !getUsedScores().includes(option.value) || option.value === field.type)
                    .map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
            
            {/* {field.type === 'toefl' && (
              <select
                className="w-1/4 rounded-md border-gray-300 border shadow-sm p-1"
                value={field.subType}
                onChange={(e) => updateToeflType(field.id, e.target.value)}
              >
                <option value="ibt">IBT</option>
                <option value="pbt">PBT</option>
              </select>
            )} */}
            
            <input
              type="number"
              className={`${field.type === 'toefl' ? 'w-1/4' : 'w-1/2'} px-2 rounded-md border-gray-300 border shadow-sm`}
              value={formData.testScores[field.type] || ''}
              onChange={(e) => updateTestScore(field.id, e.target.value)}
              placeholder="Enter score"
              disabled={!field.type}
            />
            
            <button
              onClick={() => removeField(field.id)}
              className="px-2 py-1 text-red-600 hover:text-red-800 focus:outline-none"
              disabled={activeFields.length === 1}
            >
              ×
            </button>
          </div>
        ))}
      </div>


      <button
        onClick={addNewField}
        disabled={getUsedScores().length === 8}
        className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Plus size={20} /> Add Test Score
      </button>

      <div className="flex gap-4">
        <button
          className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          onClick={() => setStep(2)}
        >
          Previous
        </button>
        <button
          className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          onClick={() => handleSubmit()}
        >
          Submit
        </button>
      </div>
    </div>
  );
}

export default StepThree;